"use client";

import React from "react";
import { Wheat, Droplets, Clock, Hand, Flame } from "lucide-react";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
const stages = [{
  id: 1,
  time: "Phase 01",
  title: "Valuation",
  desc1: "Deep financial analysis.",
  desc2: "Market-driven multiples.",
  desc3: "Establishing the baseline."
}, {
  id: 2,
  time: "Phase 02",
  title: "Packaging",
  desc1: "Premium CIM development.",
  desc2: "Strategic narrative craft.",
  desc3: "Institutional-grade data."
}, {
  id: 3,
  time: "Phase 03",
  title: "Marketing",
  desc1: "Targeted buyer outreach.",
  desc2: "Confidentiality first.",
  desc3: "Competitive bid tension."
}, {
  id: 4,
  time: "Phase 04",
  title: "Due Diligence",
  desc1: "Rigorous data verification.",
  desc2: "Risk mitigation strategy.",
  desc3: "Maintaining deal momentum."
}, {
  id: 5,
  time: "Phase 05",
  title: "Closing",
  desc1: "Final contract execution.",
  desc2: "Legacy transition secured.",
  desc3: "Maximum value realized."
}];
const icons = [{
  stageId: 1,
  Icon: Wheat
}, {
  stageId: 2,
  Icon: Droplets
}, {
  stageId: 3,
  Icon: Clock
}, {
  stageId: 4,
  Icon: Hand
}, {
  stageId: 5,
  Icon: Flame
}];
export default function The48HourJourney() {
  return <section data-section-id="223204" id="the-path-to-exit" className="dark bg-background text-foreground relative overflow-hidden py-24 lg:py-32">
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-primary/10 via-background to-background" />

      <div className="container relative z-10 mx-auto px-4 md:px-6">
        <div className="mb-16 text-center md:mb-24">
          <h2 className="font-default text-3xl font-bold tracking-tight sm:text-4xl md:text-5xl text-foreground">
            The Path to Exit
          </h2>
          <p className="mx-auto mt-4 max-w-2xl text-muted-foreground font-serif">
            A disciplined, institutional approach to sell-side M&A. We navigate the complexities of the transaction lifecycle to ensure your legacy is preserved and value is maximized.
          </p>
        </div>

        <ScrollArea className="w-full pb-8">
          <div className="relative flex min-w-max w-full justify-between px-4 sm:px-8">
            <div className="absolute left-0 right-0 top-[5rem] -z-10 h-px bg-primary" />

            {stages.map((stage, i) => {
            const IconComponent = icons.find(icon => icon.stageId === stage.id)?.Icon || Wheat;
            return <div key={stage.id} data-index={i} className="group flex w-64 flex-col items-center px-4">
                  <div className="mb-6 flex h-8 items-center justify-center">
                    <span className="font-mono text-xs uppercase tracking-widest text-muted-foreground transition-colors duration-300 group-hover:text-primary">
                      {stage.time}
                    </span>
                  </div>

                  <div className="relative mb-8 flex size-16 items-center justify-center rounded-none border border-primary/40 bg-linear-to-b from-background to-muted/20 transition-all duration-500 group-hover:border-primary group-hover:shadow-lg group-hover:shadow-primary/20">
                    <div className="absolute inset-0 rounded-none bg-primary/10 blur-xl opacity-0 transition-opacity duration-500 group-hover:opacity-100" />
                    <IconComponent className="relative z-10 size-8 text-muted-foreground transition-colors duration-500 group-hover:text-primary" strokeWidth={1.25} />
                  </div>

                  <div className="text-center">
                    <h3 className="font-default mb-3 text-xl font-semibold tracking-wide text-foreground">
                      {stage.title}
                    </h3>
                    <div className="space-y-2">
                      <p className="text-sm leading-relaxed text-muted-foreground font-serif transition-colors duration-300 group-hover:text-foreground/90">
                        {stage.desc1}
                      </p>
                      <p className="text-sm leading-relaxed text-muted-foreground font-serif transition-colors duration-300 group-hover:text-foreground/90">
                        {stage.desc2}
                      </p>
                      <p className="text-sm leading-relaxed text-muted-foreground font-serif transition-colors duration-300 group-hover:text-foreground/90">
                        {stage.desc3}
                      </p>
                    </div>
                  </div>
                </div>;
          })}
          </div>
          <ScrollBar orientation="horizontal" className="hidden sm:flex" />
        </ScrollArea>
      </div>
    </section>;
}